/**
 * Department rules for admins (D1 wave 2, workspace docs/guardian/05,
 * 2026-10-22): Guardian drafts rules from the department's documents; admins
 * review each against its source, add ones the drafting missed, and publish a
 * version. The first version waits for an Interlinked review.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  addDepartmentRule,
  approveDepartmentRule,
  editDepartmentRule,
  fetchDepartmentDocuments,
  fetchDepartmentRules,
  fetchDepartmentVersions,
  fetchDocumentCandidates,
  fetchDocumentExtraction,
  publishDepartmentVersion,
  rejectDepartmentRule,
  requeueDocumentExtraction,
  retireDepartmentRule,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';
import RuleCard, { RuleEditor } from './RuleCard';

const PAGE = 50;

export const JOB_TEXT = {
  queued: 'Waiting to be read',
  running: 'Reading for rules…',
  done: 'Finished reading',
  failed: 'Reading stopped. Try again.',
  quota_exceeded: 'This month’s reading limit is reached. Try again next month or ask Interlinked.',
};

export const VERSION_TEXT = {
  pending_cosign: 'Waiting for Interlinked review',
  published: 'Published',
  withdrawn: 'Sent back by Interlinked',
};

function Extraction({ authToken, docId }) {
  const [state, setState] = useState(null);
  const load = useCallback(async () => {
    const res = await fetchDocumentExtraction(authToken, docId);
    setState(res?.success ? res.data : { error: departmentErrorMessage(res, 'Could not load progress.') });
  }, [authToken, docId]);
  useEffect(() => {
    load();
  }, [load]);
  if (!state) return null;
  if (state.error) return <Alert severity="error">{state.error}</Alert>;
  const job = state.job;
  const canRetry = !job || job.status === 'failed' || job.status === 'quota_exceeded';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }} aria-live="polite">
      <Typography variant="body2" color="text.secondary">
        {job ? JOB_TEXT[job.status] || job.status : 'Not read for rules yet.'}
        {job && job.candidates ? ` ${job.extracted + job.noRule} of ${job.candidates} passages read.` : ''}
        {` ${state.rules} rules drafted.`}
      </Typography>
      <Button size="small" onClick={load}>Refresh</Button>
      {canRetry && (
        <Button
          size="small"
          onClick={async () => {
            const res = await requeueDocumentExtraction(authToken, docId);
            setState(res?.success ? { ...state, job: res.data.job } : { error: departmentErrorMessage(res, 'Could not start reading.') });
          }}
        >
          Read for rules
        </Button>
      )}
    </Box>
  );
}

function Coverage({ authToken, docId, onAdd }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    fetchDocumentCandidates(authToken, docId, 0, PAGE).then((res) => {
      if (live) setRows(res?.success ? res.data : { error: departmentErrorMessage(res, 'Could not load passages.') });
    });
    return () => {
      live = false;
    };
  }, [authToken, docId]);
  if (!rows) return null;
  if (rows.error) return <Alert severity="error">{rows.error}</Alert>;
  return (
    <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Passages that look like rules but produced none ({rows.total}). Add a rule if one was missed.
      </Typography>
      {rows.candidates.map((c) => (
        <Paper key={c.id} variant="outlined" component="li" sx={{ p: 1.5, listStyle: 'none' }}>
          <Typography variant="caption" color="text.secondary">
            {[c.sectionPath, c.pageFrom ? `p. ${c.pageFrom}` : null].filter(Boolean).join(' · ')}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
          <Button size="small" onClick={() => onAdd(c)}>Add rule</Button>
        </Paper>
      ))}
    </Box>
  );
}

function Versions({ authToken, onConflict }) {
  const [versions, setVersions] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const res = await fetchDepartmentVersions(authToken);
    if (res?.success) setVersions(Array.isArray(res.data?.versions) ? res.data.versions : []);
    else setError(departmentErrorMessage(res, 'Could not load versions.'));
  }, [authToken]);
  useEffect(() => {
    load();
  }, [load]);
  const publish = async () => {
    setBusy(true);
    setError(null);
    const res = await publishDepartmentVersion(authToken);
    setBusy(false);
    if (!res?.success) {
      setError(departmentErrorMessage(res, 'Could not publish.'));
      if (res?.data?.code === 'rules_conflict') onConflict();
    }
    load();
  };
  const pending = versions.some((v) => v.status === 'pending_cosign');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Typography variant="body2" color="text.secondary">
        Publishing makes your approved rules the version Guardian uses. Your first version is reviewed by Interlinked.
      </Typography>
      <Box>
        <Button variant="contained" onClick={publish} disabled={busy || pending}>Publish approved rules</Button>
      </Box>
      <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {versions.map((v) => (
          <Box component="li" key={v.version} sx={{ listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2">Version {v.version}</Typography>
            <Chip size="small" label={VERSION_TEXT[v.status] || v.status} color={v.status === 'published' ? 'success' : v.status === 'withdrawn' ? 'error' : 'default'} />
            <Typography variant="caption" color="text.secondary">
              {v.ruleCount} rules · {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString() : ''}
              {v.withdrawReason ? ` · ${v.withdrawReason}` : ''}
            </Typography>
          </Box>
        ))}
        {versions.length === 0 && <Typography variant="body2" color="text.secondary">Nothing published yet.</Typography>}
      </Box>
    </Box>
  );
}

export default function DepartmentRules({ authToken }) {
  const [tab, setTab] = useState('draft');
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState('');
  const [conflictedOnly, setConflictedOnly] = useState(false);
  const [list, setList] = useState({ total: 0, rules: [] });
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(null);
  const [off, setOff] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  useEffect(() => {
    fetchDepartmentDocuments(authToken).then((res) => {
      if (!alive.current || !res?.success) return;
      const parsed = (res.data?.documents || []).filter((d) => d.status === 'parsed');
      setDocs(parsed);
    });
  }, [authToken]);

  const load = useCallback(async () => {
    if (tab !== 'draft' && tab !== 'approved') return;
    const res = await fetchDepartmentRules(authToken, {
      status: tab, documentId: docId || undefined, conflicted: conflictedOnly, offset, limit: PAGE,
    });
    if (!alive.current) return;
    if (res?.success) setList({ total: res.data?.total || 0, rules: res.data?.rules || [] });
    else if (res?.data?.code === 'rules_unavailable') setOff(true);
    else setError(departmentErrorMessage(res, 'Could not load rules.'));
  }, [authToken, tab, docId, conflictedOnly, offset]);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (call, fallback) => {
    setError(null);
    const res = await call();
    if (!res?.success) {
      const msg = departmentErrorMessage(res, fallback);
      setError(msg);
      load();
      return msg;
    }
    load();
    return null;
  };

  const handlers = {
    onApprove: (r, ack) => act(() => approveDepartmentRule(authToken, r.id, r.rev, ack), 'Could not approve the rule.'),
    onReject: (r) => act(() => rejectDepartmentRule(authToken, r.id, r.rev), 'Could not reject the rule.'),
    onRetire: (r) => act(() => retireDepartmentRule(authToken, r.id, r.rev), 'Could not retire the rule.'),
    onEdit: (r, changes) => act(() => editDepartmentRule(authToken, r.id, { rev: r.rev, ...changes }), 'Could not save the rule.'),
  };

  // Rules not switched on for this deployment: show nothing.
  if (off) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Rules</Typography>
      <Typography variant="body2" color="text.secondary">
        Guardian drafts rules from your documents. Check each one against its source, then publish.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Tabs value={tab} onChange={(_, v) => { setTab(v); setOffset(0); }} aria-label="Rule views" variant="scrollable">
        <Tab value="draft" label="To review" />
        <Tab value="approved" label="Approved" />
        <Tab value="coverage" label="Not drafted" />
        <Tab value="versions" label="Versions" />
      </Tabs>

      {tab !== 'versions' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Select size="small" displayEmpty value={docId} onChange={(e) => { setDocId(e.target.value); setOffset(0); }} inputProps={{ 'aria-label': 'Document' }}>
            {tab !== 'coverage' && <MenuItem value="">All documents</MenuItem>}
            {tab === 'coverage' && <MenuItem value="" disabled>Choose a document</MenuItem>}
            {docs.map((d) => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
          </Select>
          {(tab === 'draft' || tab === 'approved') && (
            <FormControlLabel control={<Switch checked={conflictedOnly} onChange={(e) => { setConflictedOnly(e.target.checked); setOffset(0); }} />} label="Conflicts only" />
          )}
        </Box>
      )}
      {docId && tab !== 'versions' && <Extraction key={docId} authToken={authToken} docId={docId} />}

      {(tab === 'draft' || tab === 'approved') && (
        <>
          <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {list.rules.map((r) => <RuleCard key={`${r.id}-${r.rev}`} authToken={authToken} rule={r} {...handlers} />)}
          </Box>
          {list.rules.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {tab === 'draft' ? 'Nothing to review.' : 'No approved rules yet.'}
            </Typography>
          )}
          {list.total > PAGE && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
              <Typography variant="caption">{offset + 1}–{Math.min(offset + PAGE, list.total)} of {list.total}</Typography>
              <Button size="small" disabled={offset + PAGE >= list.total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
            </Box>
          )}
        </>
      )}
      {tab === 'coverage' && docId && <Coverage key={docId} authToken={authToken} docId={docId} onAdd={setAdding} />}
      {tab === 'versions' && (
        <Versions authToken={authToken} onConflict={() => { setTab('approved'); setConflictedOnly(true); }} />
      )}
      {adding && (
        <RuleEditor
          title="Add rule"
          onClose={() => setAdding(null)}
          onSave={async (changes) => {
            const err = await act(() => addDepartmentRule(authToken, { candidateId: adding.id, ...changes }), 'Could not add the rule.');
            if (!err) setAdding(null);
            return err;
          }}
        />
      )}
    </Paper>
  );
}
